import { useState, useEffect, useMemo } from 'react'
import useDealStore from '../store/dealStore'
import useDialogStore from '../store/dialogStore'
import { PIPELINE_STAGES } from '../data/moduleTransitions'
import ActionCenter from '../components/ActionCenter'
import '../styles/deal-dashboard.css'
import '../styles/actionCenter.css'

const moneyCompact = (value) => {
  const n = Number(value) || 0
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return `$${Math.round(n).toLocaleString()}`
}

const pct = (value) => `${(Number(value) || 0).toFixed(2)}%`

// Pipeline stage buckets, mapped from each deal's pipelineStage (modules 1-16).
const STAGE_BUCKETS = [
  { id: 'leads', label: 'Leads', note: 'New opportunities', color: '#2563eb', min: 1, max: 2 },
  { id: 'underwriting', label: 'Underwriting', note: 'In analysis', color: '#2563eb', min: 3, max: 5 },
  { id: 'loi', label: 'Capital & LOI', note: 'Financing / LOI', color: '#3b82f6', min: 6, max: 10 },
  { id: 'contract', label: 'Under Contract', note: 'In negotiation', color: '#2563eb', min: 11, max: 14 },
  { id: 'closing', label: 'Closed / Held', note: 'Post-closing', color: '#1d4ed8', min: 15, max: 16 },
]

// Normalize a persisted user deal (saved dealState snapshot) into dashboard shape.
function normalizeSavedDeal(deal) {
  const raw = deal.rawDealData || {}
  const fin = deal.extractedFinancials || {}
  const price = Number(raw.price) || 0
  const noi = Number(fin.noi) || 0
  return {
    id: deal.dealId,
    name: deal.dealName || raw.location?.address || 'Unnamed Deal',
    city: raw.location?.city || '',
    state: raw.location?.state || '',
    price,
    noi,
    capRate: noi && price ? (noi / price) * 100 : Number(raw.marketCapRate) || 0,
    buildingType: raw.property?.buildingType || 'Multifamily',
    stage: Number(deal.pipelineStage) || 1,
    status: deal.pipelineStatus || 'not_started',
    updatedAt: deal.updatedAt || deal.createdAt || null,
  }
}

function timeAgo(iso) {
  if (!iso) return ''
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

const stageGroups = [
  { label: 'Lead', modules: [1], color: '#2563eb' },
  { label: 'Analysis', modules: [2, 3, 4, 5], color: '#2563eb' },
  { label: 'Capital', modules: [6, 7, 8], color: '#3b82f6' },
  { label: 'Contract', modules: [9, 10, 11], color: '#2563eb' },
  { label: 'Closing', modules: [12, 13, 14], color: '#1d4ed8' },
  { label: 'Lifecycle', modules: [15, 16], color: '#1e40af' },
]

function buildCurrentDeal(dealState) {
  if (!dealState.dealId) return null

  const raw = dealState.rawDealData || {}
  const fin = dealState.extractedFinancials || {}
  const uw = dealState.underwritingMetrics || {}

  return {
    id: dealState.dealId,
    name: dealState.dealName || raw.location?.address || 'Current Deal',
    location: raw.location?.address || '-',
    city: raw.location?.city || '',
    state: raw.location?.state || '',
    price: Number(raw.price) || 0,
    units: Number(raw.property?.units) || Number(fin.revenue?.rentRoll?.totalUnits) || 0,
    buildingType: raw.property?.buildingType || 'Multifamily',
    capRate: Number(uw.capRate?.entry) || (Number(fin.noi) && Number(raw.price) ? (Number(fin.noi) / Number(raw.price)) * 100 : 0),
    noi: Number(fin.noi) || 0,
    score: Number(dealState.scoreAndOffer?.numericalScore) || null,
    stage: Number(dealState.pipelineStage) || 1,
    status: dealState.pipelineStatus || 'not_started',
  }
}

export default function DealDashboardPage() {
  const { dealState, userDeals, resumeDeal, deleteUserDeal, navigateToModule, getModuleStatus, getNotifications, getUnreadCount, checkDealValidation } = useDealStore()
  const confirm = useDialogStore(state => state.confirm)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    checkDealValidation()
  }, [dealState?.dealId])

  const currentDeal = buildCurrentDeal(dealState)

  // The user's real portfolio: current deal + saved deals, deduped by id.
  // Every dashboard metric derives from this — no placeholder floors, no fake trends.
  const portfolio = useMemo(() => {
    const seen = new Set()
    const list = []
    const push = (deal) => {
      if (deal && deal.id && !seen.has(deal.id)) { seen.add(deal.id); list.push(deal) }
    }
    if (currentDeal) push({ ...currentDeal, updatedAt: dealState.updatedAt || null })
    ;(userDeals || []).forEach(deal => push(normalizeSavedDeal(deal)))
    return list
  }, [currentDeal, userDeals, dealState.updatedAt])

  const isClosed = (deal) => deal.stage >= 15 || deal.status === 'completed'
  const totalDeals = portfolio.length
  const activeList = portfolio.filter(deal => !isClosed(deal))
  const activeDeals = activeList.length
  const underContract = portfolio.filter(deal => !isClosed(deal) && deal.stage >= 11 && deal.stage <= 14).length
  const closedDeals = portfolio.filter(isClosed).length

  const totalValue = portfolio.reduce((sum, deal) => sum + (Number(deal.price) || 0), 0)
  const capRates = portfolio.map(deal => Number(deal.capRate) || 0).filter(rate => rate > 0)
  const avgCapRate = capRates.length ? capRates.reduce((a, b) => a + b, 0) / capRates.length : null
  const totalNOI = portfolio.reduce((sum, deal) => {
    const noi = Number(deal.noi) || (Number(deal.price) || 0) * ((Number(deal.capRate) || 0) / 100)
    return sum + noi
  }, 0)
  const pipelineValue = activeList.reduce((sum, deal) => sum + (Number(deal.price) || 0), 0)

  const currentStage = Number(dealState.pipelineStage) || 1
  const completedModules = PIPELINE_STAGES.filter(stage => getModuleStatus(stage.id).isComplete).length
  const progressPct = Math.round((completedModules / 16) * 100)
  const alertCount = getNotifications().length
  const urgentAlertCount = getNotifications().filter(alert => ['urgent', 'overdue'].includes(alert.severity)).length
  const unreadCount = getUnreadCount()

  const emptySub = 'No deals yet — add your first deal'
  const summaryCards = [
    { label: 'Total Deals', value: totalDeals, sub: totalDeals ? 'Your deals' : emptySub, trend: null, tone: 'blue' },
    { label: 'Active Deals', value: activeDeals, sub: 'In progress', trend: null, tone: 'blue' },
    { label: 'Under Contract', value: underContract, sub: 'In negotiation', trend: null, tone: 'blue' },
    { label: 'Closed Deals', value: closedDeals, sub: 'Successfully closed', trend: null, tone: 'blue' },
    { label: 'Total Value', value: moneyCompact(totalValue), sub: 'Total deal value', trend: null, tone: 'blue-deep' },
    { label: 'Avg Cap Rate', value: avgCapRate == null ? '—' : pct(avgCapRate), sub: 'Portfolio average', trend: null, tone: 'blue' },
    { label: 'Total NOI', value: moneyCompact(totalNOI), sub: 'Net operating income', trend: null, tone: 'blue' },
    { label: 'Pipeline Value', value: moneyCompact(pipelineValue), sub: 'Active pipeline value', trend: null, tone: 'blue' },
  ]

  const liveDashboardStages = STAGE_BUCKETS.map(bucket => ({
    ...bucket,
    count: portfolio.filter(deal => deal.stage >= bucket.min && deal.stage <= bucket.max).length,
  }))

  // Deals by property type — real distribution
  const typeBreakdown = useMemo(() => {
    const counts = {}
    portfolio.forEach(deal => {
      const type = deal.buildingType || 'Multifamily'
      counts[type] = (counts[type] || 0) + 1
    })
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([type, count]) => ({ type, count, pctShare: Math.round((count / (totalDeals || 1)) * 100) }))
  }, [portfolio, totalDeals])

  // Top markets — real value by city/state
  const topMarkets = useMemo(() => {
    const byMarket = {}
    portfolio.forEach(deal => {
      const market = [deal.city, deal.state].filter(Boolean).join(', ') || 'Unknown market'
      byMarket[market] = (byMarket[market] || 0) + (Number(deal.price) || 0)
    })
    return Object.entries(byMarket).sort((a, b) => b[1] - a[1]).slice(0, 5)
  }, [portfolio])

  // Recent activity — real deal updates, newest first
  const recentActivity = useMemo(() => (
    [...portfolio]
      .filter(deal => deal.updatedAt)
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      .slice(0, 5)
      .map(deal => [
        `${deal.name} — stage ${deal.stage}/16${isClosed(deal) ? ' (closed)' : ''}`,
        timeAgo(deal.updatedAt),
      ])
  ), [portfolio])

  const insightText = currentStage <= 5
    ? 'AI recommends focusing on underwriting quality and deal score before sending an LOI.'
    : currentStage <= 11
      ? 'AI recommends prioritizing lender follow-up, LOI execution, and contract deadlines.'
      : currentStage <= 14
        ? 'AI recommends watching closing conditions, title clearance, and funding readiness.'
        : 'AI recommends monitoring hold performance and preparing exit/refinance timing.'

  return (
    <div className="deal-dashboard">
      <div className="dd-header">
        <div>
          <h1>Deal Dashboard</h1>
          <p>Real-time overview of your deal pipeline and portfolio performance</p>
        </div>
        <div className="dd-header-actions">
          <button className="dd-primary-btn" onClick={() => navigateToModule(1)}>+ Add Deal</button>
        </div>
      </div>

      <div className="dd-tabs">
        <button
          className={`dd-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`dd-tab ${activeTab === 'action-center' ? 'active' : ''}`}
          onClick={() => setActiveTab('action-center')}
        >
          Action Center
          {unreadCount > 0 && <span className="dd-tab-badge">{unreadCount}</span>}
        </button>
      </div>

      {activeTab === 'action-center' && (
        <div className="dd-action-center-tab">
          <ActionCenter />
        </div>
      )}

      {activeTab === 'overview' && <>

        <section className="dd-summary-grid">
          {summaryCards.map(card => (
            <button key={card.label} className="dd-summary-card" onClick={() => navigateToModule('dashboard')}>
              <div className={`dd-card-icon ${card.tone}`}>{card.label.charAt(0)}</div>
              <div className="dd-card-body">
                <span className="dd-card-label">{card.label}</span>
                <strong>{card.value}</strong>
                <div className="dd-card-foot">
                  <span>{card.sub}</span>
                  {card.trend && <span className="dd-trend">{card.trend} vs last month</span>}
                </div>
              </div>
            </button>
          ))}
        </section>

        <section className="dd-panel dd-pipeline-panel">
          <div className="dd-panel-title">Pipeline at a Glance</div>
          <div className="dd-stage-row">
            {liveDashboardStages.map((stage, index) => (
              <div key={stage.id} className="dd-stage-block">
                <div className="dd-stage-main">
                  <div className="dd-stage-dot" style={{ backgroundColor: stage.color }}>{stage.count}</div>
                  <div>
                    <div className="dd-stage-label">{stage.label}</div>
                    <div className="dd-stage-count">{stage.count}</div>
                    <div className="dd-stage-note">{stage.note}</div>
                  </div>
                </div>
                {index < liveDashboardStages.length - 1 && <div className="dd-stage-arrow" />}
              </div>
            ))}
          </div>
          <div className="dd-flow-line">
            {liveDashboardStages.map(stage => (
              <div key={stage.id} className="dd-flow-segment" style={{ backgroundColor: stage.color }} />
            ))}
          </div>
        </section>

        <section className="dd-main-grid">
          <div className="dd-panel dd-current-panel">
            <div className="dd-panel-title">Selected Deal Progress</div>
            <div className="dd-current-top">
              <div>
                <h2>{currentDeal?.name || 'No Deal Selected'}</h2>
                <p>{currentDeal ? `${currentDeal.location}${currentDeal.city ? `, ${currentDeal.city}` : ''}${currentDeal.state ? `, ${currentDeal.state}` : ''}` : 'Start with Module 1 to begin tracking a deal.'}</p>
                {currentDeal && (
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    marginTop: "8px",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                    backgroundColor:
                      dealState.validationStatus === 'Validated'
                        ? '#dcfce7'
                        : dealState.validationStatus === 'Rejected'
                          ? '#fee2e2'
                          : '#fef3c7',
                    color:
                      dealState.validationStatus === 'Validated'
                        ? '#166534'
                        : dealState.validationStatus === 'Rejected'
                          ? '#991b1b'
                          : '#92400e',
                    border:
                      dealState.validationStatus === 'Validated'
                        ? '1px solid #bbf7d0'
                        : dealState.validationStatus === 'Rejected'
                          ? '1px solid #fecaca'
                          : '1px solid #fde68a'
                  }}>
                    <span>🛡️</span>
                    <span>
                      Audit Status: {dealState.validationStatus || 'Pending Validation'}
                    </span>
                  </div>
                )}
              </div>
              <div className="dd-progress-ring" style={{ '--progress': `${progressPct}%` }}>
                <span>{progressPct}%</span>
              </div>
            </div>

            <div className="dd-deal-kpis">
              <div><span>Stage</span><strong>{currentStage}/16</strong></div>
              <div><span>Value</span><strong>{moneyCompact(currentDeal?.price || 0)}</strong></div>
              <div><span>Cap Rate</span><strong>{pct(currentDeal?.capRate || 0)}</strong></div>
              <div><span>NOI</span><strong>{moneyCompact(currentDeal?.noi || 0)}</strong></div>
            </div>

            <div className="dd-module-progress">
              {stageGroups.map(group => {
                const done = group.modules.filter(id => getModuleStatus(id).isComplete).length
                const active = group.modules.includes(currentStage)
                return (
                  <button
                    key={group.label}
                    className={`dd-progress-group ${active ? 'active' : ''}`}
                    onClick={() => navigateToModule(group.modules[0])}
                  >
                    <div className="dd-group-top">
                      <span className="dd-group-dot" style={{ backgroundColor: group.color }} />
                      <strong>{group.label}</strong>
                      <span>{done}/{group.modules.length}</span>
                    </div>
                    <div className="dd-group-bar">
                      <div style={{ width: `${(done / group.modules.length) * 100}%`, backgroundColor: group.color }} />
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="dd-panel dd-ai-panel">
            <div className="dd-panel-title">AI Pipeline Health</div>
            <div className="dd-health-score">
              <span>{totalDeals === 0 ? '—' : Math.min(96, Math.round(30 + progressPct * 0.4 + Math.min(activeDeals, 5) * 6 + (urgentAlertCount === 0 ? 10 : 0)))}</span>
              <div>
                <strong>{totalDeals === 0 ? 'No deals yet' : urgentAlertCount > 0 ? 'Needs attention' : 'Healthy'}</strong>
                <p>{totalDeals === 0
                  ? 'Add a deal to start tracking pipeline health.'
                  : urgentAlertCount > 0
                    ? 'Urgent items are waiting in the Action Center.'
                    : 'Pipeline is moving. Keep the next action on schedule.'}</p>
              </div>
            </div>
            <div className="dd-alert-list">
              <div className="dd-alert priority">Priority: {insightText}</div>
              <div className="dd-alert">
                {underContract > 0
                  ? `Forecast: ${underContract} under-contract deal${underContract === 1 ? '' : 's'} progressing toward closing.`
                  : 'Forecast: no deals under contract yet — focus on moving analysis-stage deals forward.'}
              </div>
              <div className="dd-alert">
                {urgentAlertCount > 0
                  ? `Attention: ${urgentAlertCount} urgent alert${urgentAlertCount === 1 ? '' : 's'} in the Action Center.`
                  : alertCount > 0
                    ? `${alertCount} open item${alertCount === 1 ? '' : 's'} in the Action Center.`
                    : 'No open alerts — pipeline is clear.'}
              </div>
            </div>
          </div>

          <div className="dd-panel">
            <div className="dd-panel-title">Deals by Property Type</div>
            <div className="dd-type-row">
              <div className="dd-donut">
                <span>{totalDeals}</span>
                <small>Total Deals</small>
              </div>
              <div className="dd-legend">
                {typeBreakdown.length === 0 && <div style={{ color: '#64748b' }}>No deals yet</div>}
                {typeBreakdown.map((entry, index) => (
                  <div key={entry.type}>
                    <span className={['blue', 'blue-deep', 'cyan', 'blue-light'][index] || 'blue'} />{' '}
                    {entry.type} <strong>{entry.pctShare}% ({entry.count})</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="dd-panel">
            <div className="dd-panel-title">Top Markets</div>
            <div className="dd-market-list">
              {topMarkets.length === 0 && <div style={{ color: '#64748b' }}>No deals yet</div>}
              {topMarkets.map(([market, value]) => (
                <div key={market}><span>{market}</span><strong>{moneyCompact(value)}</strong></div>
              ))}
            </div>
          </div>

          <div className="dd-panel">
            <div className="dd-panel-title">Recent Activity</div>
            <div className="dd-activity-list">
              {recentActivity.length === 0 && <div style={{ color: '#64748b' }}>No activity yet — start a deal to see updates here</div>}
              {recentActivity.map(([activity, time]) => (
                <div key={activity}><span>{activity}</span><strong>{time}</strong></div>
              ))}
            </div>
          </div>

          <div className="dd-panel dd-saved-deals-panel" style={{ gridColumn: '1 / -1' }}>
            <div className="dd-panel-title">Your Active & Past Deals</div>
            {(!userDeals || userDeals.length === 0) ? (
              <p style={{ color: '#64748b', margin: '8px 0 0', fontSize: '14px' }}>
                No deals saved yet. Use the "+ Add Deal" button above to start your first analysis!
              </p>
            ) : (
              <div className="dd-saved-deals-list">
                {userDeals.map(deal => {
                  const isActive = dealState?.dealId === deal.dealId;
                  const stage = deal.pipelineStage || 1;
                  const progress = Math.round((PIPELINE_STAGES.filter(s => {
                    if (deal.completedModules) return deal.completedModules.includes(s.id);
                    return s.id < stage;
                  }).length / 16) * 100);

                  return (
                    <div key={deal.dealId} className={`dd-saved-deal-card ${isActive ? 'active' : ''}`}>
                      <div className="dd-saved-deal-info">
                        <div className="dd-saved-deal-title">
                          {deal.dealName || deal.rawDealData?.location?.address || 'Unnamed Deal'}
                          {isActive && <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', background: '#2563eb', color: '#0F172A', fontWeight: 600 }}>Active</span>}
                        </div>
                        <div className="dd-saved-deal-meta">
                          <span>💰 {moneyCompact(deal.rawDealData?.price || 0)}</span>
                          <span style={{ margin: '0 8px' }}>|</span>
                          <span>🏢 {deal.rawDealData?.property?.units || deal.extractedFinancials?.revenue?.rentRoll?.totalUnits || 0} Units</span>
                          <span style={{ margin: '0 8px' }}>|</span>
                          <span>📍 {deal.rawDealData?.location?.address || 'N/A'}</span>
                          <span style={{ margin: '0 8px' }}>|</span>
                          <span>📈 Stage: {stage}/16 ({progress}% Complete)</span>
                          <span style={{ margin: '0 8px' }}>|</span>
                          <span>🛡️ Status: {deal.validationStatus || 'Pending Validation'}</span>
                        </div>
                      </div>
                      <div className="dd-saved-deal-actions">
                        <button className="dd-resume-btn" onClick={() => resumeDeal(deal.dealId)}>
                          {isActive ? 'Resume Working' : 'Load & Resume'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <div className="dd-footer-note">
          Dashboard updates in real time. Metrics reflect data across the complete AcquireOS AI platform.
        </div>

      </>}
    </div>
  )
}
